import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { 
  Phone,
  Mail,
  MessageCircle,
  MapPin,
  Clock,
  Users,
  Headphones,
  Heart,
  Building2,
  Send,
  CheckCircle,
  X
} from 'lucide-react'

interface ContactType {
  id: string
  title: string
  description: string
  icon: React.ElementType
  color: string
  bgColor: string
  phone?: string
  email: string
  whatsapp?: string
}

const contactTypes: ContactType[] = [
  {
    id: 'sales',
    title: 'Vendas & Demonstrações',
    description: 'Agende uma demo personalizada e conheça nossas soluções',
    icon: Users,
    color: 'text-kylix-secondary',
    bgColor: 'bg-gray-50',
    phone: '+55 (15) 99746-4695',
    email: 'contato@smartdataerp.com.br',
    whatsapp: '+5515997464695'
  },
  {
    id: 'support',
    title: 'Suporte Técnico',
    description: 'Ajuda com implementação, configuração e troubleshooting',
    icon: Headphones,
    color: 'text-kylix-secondary',
    bgColor: 'bg-gray-50',
    email: 'contato@smartdataerp.com.br',
    whatsapp: '+5515997464695'
  },
  {
    id: 'partnerships',
    title: 'Parcerias & Integrações',
    description: 'Torne-se parceiro ou integre sua solução conosco',
    icon: Heart,
    color: 'text-kylix-secondary',
    bgColor: 'bg-gray-50',
    email: 'contato@smartdataerp.com.br'
  },
  {
    id: 'enterprise',
    title: 'Enterprise & Corporativo',
    description: 'Soluções customizadas para grandes empresas',
    icon: Building2,
    color: 'text-kylix-secondary',
    bgColor: 'bg-gray-50',
    phone: '+55 (15) 99746-4695',
    email: 'contato@smartdataerp.com.br'
  }
]

const offices = [
  {
    city: 'Sorocaba',
    country: 'Brasil',
    address: 'SP, Brasil',
    phone: '+55 (15) 99746-4695',
    timezone: 'GMT-3',
    flag: '🇧🇷'
  }
]

export const ContactPage: React.FC = () => {
  const { t } = useTranslation('contact')
  
  // Get FAQs from translation
  const faqs = t('faqs', { returnObjects: true }) as Array<{ question: string; answer: string }>
  
  // Create contactTypes with translations
  const localizedContactTypes = contactTypes.map(contact => ({
    ...contact,
    title: t(`contactTypes.${contact.id}.title`),
    description: t(`contactTypes.${contact.id}.description`)
  }))
  
  const [selectedContact, setSelectedContact] = useState(localizedContactTypes[0])
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    employees: '',
    contactType: 'sales',
    message: '',
    urgency: 'normal'
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null
    message: string
  }>({ type: null, message: '' })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus({ type: null, message: '' })

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      const result = await response.json()

      if (response.ok && result.success) {
        setSubmitStatus({
          type: 'success',
          message: t('contactForm.successMessage')
        })
        // Limpar formulário
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          employees: '',
          contactType: 'sales',
          message: '',
          urgency: 'normal'
        })
      } else {
        setSubmitStatus({
          type: 'error',
          message: result.error || t('contactForm.errorMessage')
        })
      }
    } catch (error) {
      console.error('Erro ao enviar email:', error)
      setSubmitStatus({
        type: 'error',
        message: t('contactForm.connectionError')
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const openWhatsApp = (phone: string = '+5515997464695', message?: string) => {
    const defaultMessage = `Olá! Gostaria de conhecer mais sobre a SmartData Platform.`
    const url = `https://wa.me/${phone.replace(/\D/g, '')}?text=${encodeURIComponent(message || defaultMessage)}`
    window.open(url, '_blank')
  }

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl font-bold mb-6 text-white"
            >
              {t('hero.title')}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-lg text-gray-300 mb-8 leading-relaxed max-w-2xl mx-auto"
            >
              {t('hero.description')}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex flex-wrap gap-4 justify-center"
            >
              <button 
                onClick={() => openWhatsApp('+5515997464695')}
                className="px-6 py-3 rounded-lg font-semibold bg-kylix-secondary text-white hover:bg-kylix-primary transition-colors inline-flex items-center gap-2"
              >
                <MessageCircle className="w-5 h-5" />
                {t('hero.whatsappButton')}
              </button>
              <a 
                href="tel:+5515997464695"
                className="px-6 py-3 rounded-lg font-semibold bg-transparent border border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors inline-flex items-center gap-2"
              >
                <Phone className="w-5 h-5" />
                {t('hero.callButton')}
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Types */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl font-bold text-gray-900 mb-4"
            >
              {t('howCanWeHelp.title')}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              {t('howCanWeHelp.description')}
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {localizedContactTypes.map((contact, index) => (
              <motion.div
                key={contact.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onClick={() => setSelectedContact(contact)}
                className={`p-6 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                  selectedContact.id === contact.id
                    ? 'bg-gray-50 border-kylix-secondary'
                    : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-center mb-4">
                  <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center mx-auto mb-3">
                    <contact.icon className="w-6 h-6 text-kylix-secondary" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{contact.title}</h3>
                  <p className="text-gray-600 text-sm">{contact.description}</p>
                </div>

                <div className="space-y-3">
                  {contact.phone && (
                    <a 
                      href={`tel:${contact.phone}`}
                      className="flex items-center gap-2 text-sm text-gray-700 hover:text-kylix-primary"
                    >
                      <Phone className="w-4 h-4" />
                      {contact.phone}
                    </a>
                  )}
                  <a 
                    href={`mailto:${contact.email}`}
                    className="flex items-center gap-2 text-sm text-gray-700 hover:text-kylix-primary"
                  >
                    <Mail className="w-4 h-4" />
                    {contact.email}
                  </a>
                  {contact.whatsapp && (
                    <button 
                      onClick={() => openWhatsApp(contact.whatsapp!)}
                      className="flex items-center gap-2 text-sm text-gray-700 hover:text-green-600 w-full"
                    >
                      <MessageCircle className="w-4 h-4" />
                      WhatsApp
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Form */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg shadow-lg p-8"
              >
                <div className="mb-8">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                    {t('contactForm.title')}
                  </h3>
                  <p className="text-gray-600">
                    {t('contactForm.description')}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        {t('contactForm.labels.name')} *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kylix-secondary focus:border-transparent"
                        placeholder={t('contactForm.placeholders.name')}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        {t('contactForm.labels.email')} *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kylix-secondary focus:border-transparent"
                        placeholder={t('contactForm.placeholders.email')}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        {t('contactForm.labels.phone')}
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kylix-secondary focus:border-transparent"
                        placeholder={t('contactForm.placeholders.phone')}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        {t('contactForm.labels.company')} *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.company}
                        onChange={(e) => setFormData({...formData, company: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kylix-secondary focus:border-transparent"
                        placeholder={t('contactForm.placeholders.company')}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        {t('contactForm.labels.employees')}
                      </label>
                      <select
                        value={formData.employees}
                        onChange={(e) => setFormData({...formData, employees: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kylix-secondary focus:border-transparent"
                      >
                        <option value="">{t('contactForm.employeesOptions.select')}</option>
                        <option value="1-10">{t('contactForm.employeesOptions.1-10')}</option>
                        <option value="11-50">{t('contactForm.employeesOptions.11-50')}</option>
                        <option value="51-200">{t('contactForm.employeesOptions.51-200')}</option>
                        <option value="201-500">{t('contactForm.employeesOptions.201-500')}</option>
                        <option value="500+">{t('contactForm.employeesOptions.500+')}</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        {t('contactForm.labels.urgency')}
                      </label>
                      <select
                        value={formData.urgency}
                        onChange={(e) => setFormData({...formData, urgency: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kylix-secondary focus:border-transparent"
                      >
                        <option value="low">{t('contactForm.urgencyOptions.low')}</option>
                        <option value="normal">{t('contactForm.urgencyOptions.normal')}</option>
                        <option value="high">{t('contactForm.urgencyOptions.high')}</option>
                        <option value="urgent">{t('contactForm.urgencyOptions.urgent')}</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      {t('contactForm.labels.contactType')}
                    </label>
                    <select
                      value={formData.contactType}
                      onChange={(e) => setFormData({...formData, contactType: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kylix-secondary focus:border-transparent"
                    >
                      {localizedContactTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      {t('contactForm.labels.message')} *
                    </label>
                    <textarea
                      rows={4}
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kylix-secondary focus:border-transparent"
                      placeholder={t('contactForm.placeholders.message')}
                    />
                  </div>

                  {/* Status de envio */}
                  {submitStatus.type && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-4 rounded-lg border ${
                        submitStatus.type === 'success'
                          ? 'bg-green-50 border-green-200 text-green-800'
                          : 'bg-red-50 border-red-200 text-red-800'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {submitStatus.type === 'success' ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <X className="w-5 h-5 text-red-600" />
                        )}
                        <span className="text-sm font-medium">{submitStatus.message}</span>
                      </div>
                    </motion.div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full text-lg py-4 rounded-lg font-semibold transition-all inline-flex items-center justify-center gap-2 ${
                      isSubmitting
                        ? 'bg-gray-400 cursor-not-allowed text-white'
                        : 'btn-primary hover:bg-kylix-primary'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        {t('contactForm.submitting')}
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        {t('contactForm.submitButton')}
                      </>
                    )}
                  </button>
                </form>
              </motion.div>

              {/* Office Info & FAQ */}
              <div className="space-y-8">
                {/* Offices */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-lg shadow-lg p-8"
                >
                  <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                    <MapPin className="w-6 h-6 text-kylix-secondary" />
                    {t('location.title')}
                  </h3>
                  
                  <div className="space-y-4">
                    {offices.map((office, index) => (
                      <div key={index} className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-start gap-3">
                          <span className="text-2xl">{office.flag}</span>
                          <div>
                            <h4 className="font-semibold text-gray-900">{office.city}, {office.country}</h4>
                            <p className="text-sm text-gray-600 mb-2">{office.address}</p>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {office.phone}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {office.timezone}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Quick FAQ */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-lg shadow-lg p-8"
                >
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">
                    {t('faqSection.title')}
                  </h3>
                  
                  <div className="space-y-4">
                    {faqs.map((faq, index) => (
                      <details key={index} className="group border-b border-gray-100 pb-4">
                        <summary className="flex items-center justify-between cursor-pointer py-2 text-sm font-medium text-gray-900 hover:text-kylix-secondary">
                          {faq.question}
                          <span className="ml-2 group-open:rotate-180 transition-transform text-kylix-secondary">▼</span>
                        </summary>
                        <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                          {faq.answer}
                        </p>
                      </details>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Contact Bar */}
      <section className="py-12 bg-gray-900 text-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="p-6 bg-gray-800 rounded-lg"
              >
                <Phone className="w-8 h-8 mx-auto mb-3 text-kylix-secondary" />
                <h3 className="font-semibold mb-2">{t('quickContact.phone.title')}</h3>
                <a 
                  href="tel:+5515997464695"
                  className="text-lg hover:text-kylix-secondary transition-colors block"
                >
                  +55 (15) 99746-4695
                </a>
                <p className="text-sm text-gray-400 mt-1">{t('quickContact.phone.description')}</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="p-6 bg-gray-800 rounded-lg"
              >
                <MessageCircle className="w-8 h-8 mx-auto mb-3 text-kylix-secondary" />
                <h3 className="font-semibold mb-2">{t('quickContact.whatsapp.title')}</h3>
                <button 
                  onClick={() => openWhatsApp('+5515997464695')}
                  className="text-lg hover:text-kylix-secondary transition-colors"
                >
                  {t('quickContact.whatsapp.buttonText')}
                </button>
                <p className="text-sm text-gray-400 mt-1">{t('quickContact.whatsapp.description')}</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="p-6 bg-gray-800 rounded-lg"
              >
                <Mail className="w-8 h-8 mx-auto mb-3 text-kylix-secondary" />
                <h3 className="font-semibold mb-2">{t('quickContact.email.title')}</h3>
                <a 
                  href="mailto:contato@smartdataerp.com.br"
                  className="text-lg hover:text-kylix-secondary transition-colors block"
                >
                  contato@smartdataerp.com.br
                </a>
                <p className="text-sm text-gray-400 mt-1">{t('quickContact.email.description')}</p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}